import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtTRY } from "../lib/format";
import { PageHeader } from "../components/Common";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const nf = (v) => new Intl.NumberFormat("tr-TR", { notation: "compact", maximumFractionDigits: 1 }).format(v);
const budgetExpense = (b) => (b.insurance || 0) + (b.mtv || 0) + (b.maintenance || 0) + (b.financing || 0) + (b.tires || 0) + (b.other || 0);
const budgetIncome = (b) => (b.expected_rental_income || 0) + (b.expected_sale || 0);

export default function ButceAnaliz() {
  const [vehicles, setVehicles] = useState([]);
  const [prof, setProf] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then(({ data }) => setVehicles(data));
    api.get("/vehicle-profitability").then(({ data }) => setProf(data));
  }, []);

  const profByPlate = Object.fromEntries(prof.map((r) => [r.plate, r]));
  const rows = vehicles.filter((v) => v.budget).map((v) => {
    const b = v.budget;
    const projExp = budgetExpense(b) + (v.purchase_price || 0);
    const projInc = budgetIncome(b);
    const projProfit = projInc - projExp;
    const a = profByPlate[v.plate] || { income: 0, expense: 0, profit: 0 };
    return { plate: v.plate, vehicle: `${v.brand} ${v.model}`, status: v.status, projInc, projExp, projProfit, actInc: a.income, actExp: a.expense, actProfit: a.profit, variance: a.profit - projProfit };
  });

  return (
    <div className="p-8 max-w-[1400px]">
      <PageHeader title="Bütçe vs Gerçekleşen" subtitle="Araç öngörü bütçeleri ile gerçekleşen kâr/zararın karşılaştırması" />

      {rows.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-200 rounded-sm p-12 text-center text-zinc-400 animate-fade-up" data-testid="budget-empty">
          Henüz bütçe öngörüsü girilmiş araç yok. Envanter sayfasında bir aracın <strong>Bütçe</strong> butonundan öngörü ekleyin.
        </div>
      ) : (
        <>
          <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-4 animate-fade-up" data-testid="budget-chart">
            <h3 className="font-display text-lg font-semibold text-zinc-900 mb-4">Öngörü vs Gerçekleşen Kâr</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={rows.map((r) => ({ name: r.plate, "Öngörü Kâr": r.projProfit, "Gerçek Kâr": r.actProfit }))} margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f4" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#71717a", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={nf} tick={{ fontSize: 11, fill: "#a1a1aa", fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <Tooltip formatter={(v) => fmtTRY(v)} contentStyle={{ borderRadius: 4, border: "1px solid #e4e4e7", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Öngörü Kâr" fill="#a1a1aa" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Gerçek Kâr" fill="#002FA7" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden animate-fade-up" data-testid="budget-analysis-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Plaka / Araç</th>
                <th className="text-right font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Öngörü Gelir</th>
                <th className="text-right font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Gerçek Gelir</th>
                <th className="text-right font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Öngörü Gider</th>
                <th className="text-right font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Gerçek Gider</th>
                <th className="text-right font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Öngörü Kâr</th>
                <th className="text-right font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Gerçek Kâr</th>
                <th className="text-right font-semibold text-zinc-600 px-3 py-3 text-xs uppercase tracking-wider">Sapma</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.plate} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`budget-row-${r.plate}`}>
                  <td className="px-3 py-3">
                    <div className="font-mono font-semibold text-zinc-900">{r.plate}</div>
                    <div className="text-xs text-zinc-500">{r.vehicle}</div>
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-zinc-500 tabular-nums">{fmtTRY(r.projInc)}</td>
                  <td className="px-3 py-3 text-right font-mono text-green-600 tabular-nums">{fmtTRY(r.actInc)}</td>
                  <td className="px-3 py-3 text-right font-mono text-zinc-500 tabular-nums">{fmtTRY(r.projExp)}</td>
                  <td className="px-3 py-3 text-right font-mono text-red-600 tabular-nums">{fmtTRY(r.actExp)}</td>
                  <td className={`px-3 py-3 text-right font-mono tabular-nums ${r.projProfit >= 0 ? "text-zinc-700" : "text-red-600"}`}>{fmtTRY(r.projProfit)}</td>
                  <td className={`px-3 py-3 text-right font-mono font-semibold tabular-nums ${r.actProfit >= 0 ? "text-zinc-900" : "text-red-600"}`}>{fmtTRY(r.actProfit)}</td>
                  <td className={`px-3 py-3 text-right font-mono font-semibold tabular-nums ${r.variance >= 0 ? "text-green-600" : "text-red-600"}`}>{r.variance >= 0 ? "+" : ""}{fmtTRY(r.variance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
      <p className="text-xs text-zinc-400 mt-3">Öngörü gider = sigorta + MTV + bakım + finansman + kış lastiği + diğer + alım bedeli. Öngörü gelir = kira tahmini + satış öngörüsü. Gerçek değerler işlem kayıtlarından (alım/satış/faiz dahil) hesaplanır.</p>
    </div>
  );
}
